import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Tooltip,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  ArrowBack as ArrowBackIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  CloudUpload as CloudUploadIcon,
  Folder as FolderIcon,
  GetApp as DownloadIcon,
  InsertDriveFile as FileIcon,
  OpenInNew as OpenInNewIcon,
  UnfoldMore as UnfoldMoreIcon
} from '@material-ui/icons';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import sharePointExplorerService from '../../../../../services/SharePointExplorer';

const NOT_AUTHORISED_MESSAGE = 'You\u2019re not authorised to get access.';

const useStyles = makeStyles((theme) => ({
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: theme.palette.grey[50],
    padding: '0 8px',
    minHeight: 40,
    userSelect: 'none'
  },
  colName: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center'
  },
  colModified: {
    width: 180,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center'
  },
  colModifiedBy: {
    width: 160,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center'
  },
  colActions: {
    width: 80,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  sortButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: '4px 6px',
    borderRadius: 4,
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary
    },
    '& svg': {
      fontSize: '0.95rem'
    }
  },
  sortButtonActive: {
    color: theme.palette.primary.main
  },
  colLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    padding: '4px 6px'
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 8px',
    minHeight: 44,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    },
    '&:hover $rowActions': {
      opacity: 1
    }
  },
  rowDivider: {
    borderBottom: '1px solid #f0f0f0'
  },
  rowName: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    overflow: 'hidden'
  },
  rowNameText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.875rem'
  },
  rowModified: {
    width: 180,
    flexShrink: 0,
    fontSize: '0.8rem',
    color: theme.palette.text.secondary
  },
  rowModifiedBy: {
    width: 160,
    flexShrink: 0,
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  rowActions: {
    width: 80,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    opacity: 0,
    transition: 'opacity 0.15s'
  }
}));

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <UnfoldMoreIcon />;
  return sortDir === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />;
};

SortIcon.propTypes = {
  field: PropTypes.string.isRequired,
  sortField: PropTypes.string.isRequired,
  sortDir: PropTypes.string.isRequired
};

const SharePointExplorer = ({ customerId, customerName }) => {
  const classes = useStyles();
  const [items, setItems] = useState([]);
  const [driveId, setDriveId] = useState(null);
  const [folderSegments, setFolderSegments] = useState([]);
  const [rootPath, setRootPath] = useState('');
  const [savedLocation, setSavedLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [canUpload, setCanUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadProgress, setUploadProgress] = useState({ completed: 0, total: 0 });
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [downloadingIds, setDownloadingIds] = useState({});
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [locationFolders, setLocationFolders] = useState([]);
  const [locationStack, setLocationStack] = useState([]);
  const [locationDriveId, setLocationDriveId] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationSaving, setLocationSaving] = useState(false);
  const [locationError, setLocationError] = useState('');

  // eslint-disable-next-line no-undef
  const isAdmin = localStorage.getItem('role') === 'Admin';

  const folderPath = folderSegments.join('/');

  useEffect(() => {
    let active = true;

    const fetchItems = async () => {
      if (!customerName) {
        setItems([]);
        setError('A customer is required to load SharePoint documents.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response = await sharePointExplorerService.getDriveItems(customerName, folderPath);
        if (active) {
          setItems(response.data?.data?.items || []);
          setDriveId(response.data?.data?.driveId || null);
          setRootPath(response.data?.data?.rootPath || '');
          setSavedLocation(response.data?.data?.savedLocation || null);
          setCanUpload(Boolean(response.data?.data?.canUpload));
        }
      } catch (requestError) {
        if (active) {
          setItems([]);
          setCanUpload(false);
          setError(
            requestError.response?.status === 403
              ? NOT_AUTHORISED_MESSAGE
              : requestError.response?.data?.message || 'Could not load SharePoint documents.'
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchItems();

    return () => {
      active = false;
    };
  }, [customerName, folderPath, refreshVersion]);

  const sortedItems = useMemo(() => {
    const folders = items.filter((i) => i.folder);
    const files = items.filter((i) => !i.folder);

    const compare = (a, b) => {
      let valA, valB;
      if (sortField === 'name') {
        valA = (a.name || '').toLowerCase();
        valB = (b.name || '').toLowerCase();
      } else if (sortField === 'modified') {
        valA = a.lastModifiedDateTime || '';
        valB = b.lastModifiedDateTime || '';
      } else {
        return 0;
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    };

    return [...folders.sort(compare), ...files.sort(compare)];
  }, [items, sortField, sortDir]);

  const handleSort = (field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return field;
      }
      setSortDir('asc');
      return field;
    });
  };

  const handleItemClick = (item) => {
    if (item.folder) {
      setFolderSegments((currentSegments) => [...currentSegments, item.name]);
    }
  };

  const handleBack = () => {
    setFolderSegments((currentSegments) => currentSegments.slice(0, -1));
  };

  const handleBreadcrumbClick = (folderIndex) => {
    setFolderSegments((currentSegments) => currentSegments.slice(0, folderIndex));
  };

  const handleDownload = async (event, item) => {
    event.stopPropagation();
    if (!driveId) return;
    setDownloadingIds((prev) => ({ ...prev, [item.id]: true }));
    try {
      const response = await sharePointExplorerService.downloadFile(driveId, item.id, customerName);
      // eslint-disable-next-line no-undef
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      // eslint-disable-next-line no-undef
      const url = URL.createObjectURL(blob);
      // eslint-disable-next-line no-undef
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = item.name;
      anchor.click();
      // eslint-disable-next-line no-undef
      URL.revokeObjectURL(url);
    } catch {
      // Silently ignore — user can still open via webUrl
    } finally {
      setDownloadingIds((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const getLocationErrorMessage = (requestError) =>
    requestError.response?.status === 403
      ? NOT_AUTHORISED_MESSAGE
      : requestError.response?.data?.message || 'Could not load SharePoint folders.';

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    setUploading(true);
    setUploadError('');
    setUploadProgress({ completed: 0, total: files.length });
    const uploadTimestamp = Date.now().toString();
    let uploadedCount = 0;
    const failures = [];

    for (const [index, file] of files.entries()) {
      setUploadProgress({ completed: index, total: files.length });
      try {
        await sharePointExplorerService.uploadFile(file, customerName, folderPath, {
          uploadTimestamp,
          uploadIndex: index,
          uploadCount: files.length
        });
        uploadedCount += 1;
      } catch (requestError) {
        const message =
          requestError.response?.status === 403
            ? NOT_AUTHORISED_MESSAGE
            : requestError.response?.data?.message || `Could not upload ${file.name}.`;
        failures.push({ fileName: file.name, message });
        if (requestError.response?.status === 403) {
          setCanUpload(false);
          break;
        }
      }
    }

    if (uploadedCount > 0) {
      setRefreshVersion((currentVersion) => currentVersion + 1);
    }
    if (failures.length > 0) {
      const firstFailure = failures[0];
      setUploadError(
        failures.length === 1
          ? `${firstFailure.fileName}: ${firstFailure.message}`
          : `${failures.length} files could not be uploaded. ${firstFailure.fileName}: ${firstFailure.message}`
      );
    }
    setUploadProgress({ completed: files.length, total: files.length });
    setUploading(false);
  };

  const loadLocationFolder = async (folderItemId, nextStack) => {
    setLocationLoading(true);
    setLocationError('');
    try {
      const response = await sharePointExplorerService.getLocationFolders(folderItemId);
      const data = response.data?.data;
      setLocationDriveId(data?.driveId || null);
      setLocationFolders(data?.folders || []);
      setLocationStack(nextStack(data?.currentFolder));
    } catch (requestError) {
      setLocationFolders([]);
      setLocationError(getLocationErrorMessage(requestError));
    } finally {
      setLocationLoading(false);
    }
  };

  const handleOpenLocationDialog = async () => {
    setLocationDialogOpen(true);
    setLocationError('');
    setLocationStack([]);
    setLocationFolders([]);
    await loadLocationFolder(undefined, (currentFolder) => (currentFolder ? [currentFolder] : []));
  };

  const handleLocationFolderClick = (folder) =>
    loadLocationFolder(folder.id, (currentFolder) => [...locationStack, currentFolder]);

  const handleLocationBreadcrumbClick = (index) => {
    const folder = locationStack[index];
    if (!folder || index === locationStack.length - 1) return;
    loadLocationFolder(folder.id, (currentFolder) => [...locationStack.slice(0, index), currentFolder]);
  };

  const handleSaveLocation = async () => {
    const currentFolder = locationStack[locationStack.length - 1];
    if (!currentFolder) return;

    setLocationSaving(true);
    setLocationError('');
    try {
      const response = await sharePointExplorerService.updateCustomerLocation({
        customerId,
        customerName,
        driveId: locationDriveId || currentFolder.parentReference?.driveId || driveId,
        driveItemId: currentFolder.id
      });
      setSavedLocation(response.data?.data || null);
      setFolderSegments([]);
      setLocationDialogOpen(false);
      setRefreshVersion((currentVersion) => currentVersion + 1);
    } catch (requestError) {
      setLocationError(getLocationErrorMessage(requestError));
    } finally {
      setLocationSaving(false);
    }
  };

  return (
    <Paper variant="outlined">
      {/* Toolbar */}
      <Box display="flex" alignItems="center" minHeight={56} paddingX={2} borderBottom="1px solid #e0e0e0">
        {folderSegments.length > 0 && (
          <IconButton aria-label="Go back one folder" onClick={handleBack} edge="start">
            <ArrowBackIcon />
          </IconButton>
        )}
        <Breadcrumbs aria-label="SharePoint folder path" style={{ flex: 1 }}>
          {folderSegments.length > 0 ? (
            <Link component="button" color="inherit" onClick={() => handleBreadcrumbClick(0)} type="button">
              {rootPath || customerName}
            </Link>
          ) : (
            <Typography color="textPrimary" variant="subtitle1">
              {rootPath || customerName}
            </Typography>
          )}
          {folderSegments.map((segment, index) =>
            index === folderSegments.length - 1 ? (
              <Typography color="textPrimary" key={`${segment}-${index}`} variant="subtitle1">
                {segment}
              </Typography>
            ) : (
              <Link
                component="button"
                color="inherit"
                key={`${segment}-${index}`}
                onClick={() => handleBreadcrumbClick(index + 1)}
                type="button"
                underline="hover"
              >
                {segment}
              </Link>
            )
          )}
        </Breadcrumbs>
        <Box display="flex" gridGap={8}>
          {canUpload && (
            <Button
              color="primary"
              component="label"
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
              variant="contained"
            >
              {uploading
                ? `Uploading ${Math.min(uploadProgress.completed + 1, uploadProgress.total)} of ${
                    uploadProgress.total
                  }\u2026`
                : 'Upload files'}
              <input hidden multiple type="file" onChange={handleUpload} />
            </Button>
          )}
          {isAdmin && (
            <Button color="primary" onClick={handleOpenLocationDialog} variant="outlined">
              Update Folder Location
            </Button>
          )}
        </Box>
      </Box>

      {uploadError && (
        <Box paddingX={2} paddingY={1}>
          <Typography color="error">{uploadError}</Typography>
        </Box>
      )}

      {savedLocation && (
        <Box paddingX={2} paddingY={1}>
          <Typography color="textSecondary" variant="caption">
            Photo upload location: /{savedLocation.folderPath}
          </Typography>
        </Box>
      )}

      <Dialog fullWidth maxWidth="sm" open={locationDialogOpen} onClose={() => setLocationDialogOpen(false)}>
        <DialogTitle>Update Folder Location</DialogTitle>
        <DialogContent dividers>
          <Typography color="textSecondary" gutterBottom>
            Choose where this customer&apos;s images should be saved. Paths at least three folders deep are used
            directly. For shallower paths, the customer folder and its Photos folder will be created automatically.
            Folder browsing starts at /Projects.
          </Typography>
          {locationStack.length > 0 && (
            <Breadcrumbs aria-label="Location folder path" style={{ marginBottom: 12 }}>
              {locationStack.map((folder, index) =>
                index === locationStack.length - 1 ? (
                  <Typography color="textPrimary" key={folder.id}>
                    {folder.name}
                  </Typography>
                ) : (
                  <Link
                    component="button"
                    color="inherit"
                    key={folder.id}
                    onClick={() => handleLocationBreadcrumbClick(index)}
                    type="button"
                  >
                    {folder.name}
                  </Link>
                )
              )}
            </Breadcrumbs>
          )}
          {locationError && <Typography color="error">{locationError}</Typography>}
          {locationLoading ? (
            <Box display="flex" justifyContent="center" padding={4}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box>
              {locationFolders.map((folder) => (
                <Button
                  fullWidth
                  key={folder.id}
                  onClick={() => handleLocationFolderClick(folder)}
                  startIcon={<FolderIcon color="primary" />}
                  style={{ justifyContent: 'flex-start', marginBottom: 4 }}
                >
                  {folder.name}
                </Button>
              ))}
              {!locationError && locationFolders.length === 0 && (
                <Typography color="textSecondary">This folder has no subfolders.</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationDialogOpen(false)} disabled={locationSaving}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={locationLoading || locationSaving || locationStack.length === 0}
            onClick={handleSaveLocation}
            variant="contained"
          >
            {locationSaving ? 'Saving\u2026' : 'Use This Folder'}
          </Button>
        </DialogActions>
      </Dialog>

      {loading && (
        <Box display="flex" justifyContent="center" padding={6}>
          <CircularProgress aria-label="Loading SharePoint documents" />
        </Box>
      )}

      {!loading && error && (
        <Box padding={4}>
          <Typography color="error" align="center">
            {error}
          </Typography>
        </Box>
      )}

      {!loading && !error && items.length === 0 && (
        <Box padding={4}>
          <Typography color="textSecondary" align="center">
            This folder is empty.
          </Typography>
        </Box>
      )}

      {!loading && !error && items.length > 0 && (
        <>
          {/* Column headers */}
          <div className={classes.tableHeader}>
            <div className={classes.colName}>
              <button
                className={`${classes.sortButton} ${sortField === 'name' ? classes.sortButtonActive : ''}`}
                onClick={() => handleSort('name')}
                type="button"
              >
                Name
                <SortIcon field="name" sortField={sortField} sortDir={sortDir} />
              </button>
            </div>
            <div className={classes.colModified}>
              <button
                className={`${classes.sortButton} ${sortField === 'modified' ? classes.sortButtonActive : ''}`}
                onClick={() => handleSort('modified')}
                type="button"
              >
                Modified
                <SortIcon field="modified" sortField={sortField} sortDir={sortDir} />
              </button>
            </div>
            <div className={classes.colModifiedBy}>
              <span className={classes.colLabel}>Modified By</span>
            </div>
            <div className={classes.colActions} />
          </div>

          {/* Rows */}
          {sortedItems.map((item, index) => (
            <div
              key={item.id}
              className={`${classes.row} ${index < sortedItems.length - 1 ? classes.rowDivider : ''}`}
              onClick={() => handleItemClick(item)}
              role={item.folder ? 'button' : undefined}
              tabIndex={item.folder ? 0 : undefined}
              onKeyDown={item.folder ? (e) => e.key === 'Enter' && handleItemClick(item) : undefined}
            >
              {/* Name */}
              <div className={classes.rowName}>
                {item.folder ? (
                  <FolderIcon color="primary" style={{ fontSize: 20, flexShrink: 0 }} />
                ) : (
                  <FileIcon style={{ fontSize: 20, flexShrink: 0, color: '#666' }} />
                )}
                <Tooltip title={item.name} placement="top-start" enterDelay={600}>
                  <span className={classes.rowNameText}>{item.name}</span>
                </Tooltip>
                {item.folder && (
                  <Typography variant="caption" color="textSecondary" style={{ flexShrink: 0 }}>
                    {item.folder.childCount != null
                      ? `${item.folder.childCount} item${item.folder.childCount === 1 ? '' : 's'}`
                      : ''}
                  </Typography>
                )}
              </div>

              {/* Modified */}
              <div className={classes.rowModified}>{formatDate(item.lastModifiedDateTime)}</div>

              {/* Modified By */}
              <div className={classes.rowModifiedBy}>
                <Tooltip title={item.lastModifiedBy?.user?.displayName || ''} placement="top-start" enterDelay={400}>
                  <span>{item.lastModifiedBy?.user?.displayName || ''}</span>
                </Tooltip>
              </div>

              {/* Actions */}
              <div
                className={classes.rowActions}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                role="presentation"
              >
                {!item.folder && (
                  <>
                    <Tooltip title="Download" className="tw-mr-3">
                      <span>
                        <IconButton
                          aria-label={`Download ${item.name}`}
                          disabled={downloadingIds[item.id]}
                          onClick={(e) => handleDownload(e, item)}
                          size="small"
                        >
                          {downloadingIds[item.id] ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Open in SharePoint">
                      <IconButton
                        aria-label={`Open ${item.name} in SharePoint`}
                        // eslint-disable-next-line no-undef
                        onClick={() => window.open(item.webUrl, '_blank', 'noopener,noreferrer')}
                        size="small"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </Paper>
  );
};

SharePointExplorer.propTypes = {
  customerId: PropTypes.string,
  customerName: PropTypes.string
};

export default SharePointExplorer;
